import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UserModel, UserResponse, CreateUserRequest, UpdateUserRequest } from '../models/UserModel';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { getApiBaseUrl } from '@/setup/apiBaseUrl';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, { getState }) => {
      // Get token from Redux state if needed
      const state = getState() as any;
      const accessToken = state.auth?.accessToken;
      
      // Always add Authorization header if we have a token
      if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`);
      }

      const selectedCompanyId = getCurrentCompanyId();
      if (selectedCompanyId) {
        headers.set('X-Selected-Company', selectedCompanyId);
      }
      
      headers.set('content-type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['User'],
  keepUnusedDataFor: 300, // 5 minutes cache
  endpoints: (builder) => ({
    // Get users list
    getUsers: builder.query<UserResponse, any>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        const selectedCompanyId = getCurrentCompanyId();
        const isGlobalRoleFilter = params?.role === 'global';
        if (params) {
          if (params.search) {
            searchParams.append('search', params.search);
          }
          // Backend expects getlist for status and role, so we need to append multiple times
          if (params.status) {
            searchParams.append('status', params.status);
          }
          if (params.role) {
            searchParams.append('role', params.role);
          }
          if (!isGlobalRoleFilter && params.company) {
            searchParams.append('company', params.company);
          } else if (!isGlobalRoleFilter && selectedCompanyId) {
            searchParams.append('company', selectedCompanyId);
          }
          if (params.page) {
            searchParams.append('page', String(params.page));
          }
          if (params.page_size) {
            searchParams.append('page_size', String(params.page_size));
          }
          if (params.ordering) {
            searchParams.append('ordering', params.ordering);
          }
        }
        const queryString = searchParams.toString();
        return `accounts/users/${queryString ? '?' + queryString : ''}`;
      },
      providesTags: ['User'],
    }),
    
    // Get user by ID
    getUserById: builder.query<UserModel, string>({
      query: (id) => `accounts/users/${id}/`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    
    // Create user
    createUser: builder.mutation<UserModel, CreateUserRequest>({
      query: (userData) => ({
        url: 'accounts/users/',
        method: 'POST',
        body: {
          ...userData,
          company: userData.company || getCurrentCompanyId(),
        },
      }),
      invalidatesTags: ['User'],
    }),
    
    // Update user
    updateUser: builder.mutation<UserModel, { id: string; data: UpdateUserRequest }>({
      query: ({ id, data }) => ({
        url: `accounts/users/${id}/`,
        method: 'PATCH',
        body: {
          ...data,
          company: data.company || getCurrentCompanyId(),
        },
      }),
      invalidatesTags: ['User'],
    }),
    
    // Delete user
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `accounts/users/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = usersApi;

